import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "accounting-reports-page" }
const _hoisted_2 = { class: "reports-page-toolbar" }
const _hoisted_3 = { class: "toolbar-title" }
const _hoisted_4 = { class: "title-icon" }
const _hoisted_5 = { class: "toolbar-banks" }
const _hoisted_6 = ["onClick"]
const _hoisted_7 = {
  key: 0,
  class: "bank-empty"
}
const _hoisted_8 = {
  key: 0,
  class: "reports-page-loading"
}
const _hoisted_9 = {
  key: 1,
  class: "reports-page-body"
}

import { ref, computed, watch, inject, onMounted, type Ref } from 'vue';
import { useRoute } from 'vue-router';
import { useMemberYearMonthOptional } from '@/composables/useMemberYearMonth';
import { bankbookApi, beneficiaryApi, collectionApi } from '@/services/api';
import BankbookReportsModal from '@/components/bankbook/BankbookReportsModal.vue';

interface BankData {
  mb_code: string;
  mb_alias: string;
  mb_bank_name: string;
  mb_biz_gubun?: string;
  mb_start_date?: string;
  mb_end_date?: string;
}

interface AccountCode {
  gubun: 'IN' | 'OUT';
  scode: string;
  sname: string;
}

interface CashbookItem {
  cb_idx?: number;
  cb_bank_code?: string;
  cb_scode?: string;
  cb_memo?: string;
  cb_price?: number;
  cb_matched_beneficiary_idx?: number | null;
  split_count?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'MemberDetailAccountingReports',
  setup(__props) {

const route = useRoute();
const memberData = inject<any>('memberData');
const selectedBizGubunRef = inject<Ref<string>>('selectedBizGubun');
const selectedBizGubun = computed(() => selectedBizGubunRef?.value || '');

const memberCode = computed(() => {
  return route.params.memberCode as string || memberData?.value?.m_code || 0;
});

const memberCodeForYm = computed(() => String(memberCode.value || ''));
const memberYearMonth = useMemberYearMonthOptional(memberCodeForYm);
const currentYear = computed(() => String(memberYearMonth.selectedYear.value));
const currentMonth = computed(() => String(memberYearMonth.selectedMonth.value).padStart(2, '0'));

const isLoading = ref(true);
const initialDataLoaded = ref(false);

const bankList = ref<BankData[]>([]);
const selectedBankCode = ref<string | null>(null);
const accountCodes = ref<AccountCode[]>([]);
const cashbookList = ref<CashbookItem[]>([]);
const prevMonthBalance = ref(0);
const beneficiaryMatches = ref<Record<number, { cb_idx: number; cb_name: string; matchType: string }>>({});

const incomeCodeList = computed(() => accountCodes.value.filter((ac) => ac.gubun === 'IN'));
const expenseCodeList = computed(() => accountCodes.value.filter((ac) => ac.gubun === 'OUT'));

const isBankActiveForCurrentMonth = (bank: BankData): boolean => {
  const currentYM = `${currentYear.value}-${currentMonth.value}`;
  if (!bank.mb_start_date) return true;
  if (currentYM < bank.mb_start_date) return false;
  if (bank.mb_end_date && currentYM > bank.mb_end_date) return false;
  return true;
};

const activeBankList = computed(() => bankList.value.filter((bank) => isBankActiveForCurrentMonth(bank)));

const selectedBankAlias = computed(() => {
  if (!selectedBankCode.value) return '';
  const bank = bankList.value.find((b) => b.mb_code === selectedBankCode.value);
  return bank?.mb_alias || bank?.mb_bank_name || '통장';
});

const reportCashbookList = computed(() => {
  if (!selectedBankCode.value) return cashbookList.value;
  return cashbookList.value.filter((item) => item.cb_bank_code === selectedBankCode.value);
});

const selectBank = (bankCode: string | null) => {
  selectedBankCode.value = bankCode;
};

const onChangeYearMonth = (year: string, month: string) => {
  memberYearMonth.setYearMonth(parseInt(year, 10), parseInt(month, 10));
};

const loadBankList = async () => {
  if (!memberCode.value) return;
  try {
    const res = await bankbookApi.getBankList(memberCode.value, true);
    let allBanks = (res.data?.data || []) as BankData[];
    if (selectedBizGubun.value) {
      allBanks = allBanks.filter((b) => b.mb_biz_gubun === selectedBizGubun.value);
    }
    bankList.value = allBanks;
    if (selectedBankCode.value !== null) {
      const exists = activeBankList.value.some((b) => b.mb_code === selectedBankCode.value);
      if (!exists) selectedBankCode.value = null;
    }
  } catch (error) {
    console.error('통장 목록 로드 오류:', error);
    bankList.value = [];
  }
};

const loadAccountCodes = async () => {
  try {
    const res = await bankbookApi.getAccountCodes(currentYear.value);
    accountCodes.value = (res.data?.data || []) as AccountCode[];
  } catch (error) {
    console.error('계정코드 로드 오류:', error);
  }
};

const loadCarryover = async () => {
  const monthNum = parseInt(currentMonth.value, 10);
  const yearNum = parseInt(currentYear.value, 10);
  if (monthNum <= 1 || !memberCode.value) {
    prevMonthBalance.value = 0;
    return;
  }
  try {
    const res = await bankbookApi.getCumulativeCarryover(
      memberCode.value,
      String(yearNum),
      String(monthNum).padStart(2, '0'),
      selectedBizGubun.value || undefined,
      selectedBankCode.value || undefined
    );
    prevMonthBalance.value = Number(res.data?.data?.balance) || 0;
  } catch (error) {
    console.error('이월금 조회 오류:', error);
    prevMonthBalance.value = 0;
  }
};

const matchBeneficiaries = async () => {
  if (!memberCode.value) {
    beneficiaryMatches.value = {};
    return;
  }

  const targetItems = cashbookList.value
    .filter((item) => item.cb_scode === '112')
    .map((item) => ({
      idx: item.cb_idx!,
      memo: item.cb_memo || '',
      price: item.cb_price || 0,
      matchedBeneficiaryIdx: item.cb_matched_beneficiary_idx,
    }));

  if (targetItems.length === 0) {
    beneficiaryMatches.value = {};
    return;
  }

  try {
    const collectionRes = await collectionApi.getByMember(memberCode.value, {
      year: parseInt(currentYear.value, 10),
      month: parseInt(currentMonth.value, 10),
      bizGubun: selectedBizGubun.value || undefined,
    });
    const collectionData = collectionRes.data?.success
      ? (collectionRes.data.data?.list || collectionRes.data.data || [])
      : [];

    const beneficiaryNameMap = new Map<number, string>();
    collectionData.forEach((item: any) => {
      if (item.cb_idx && item.cb_name) {
        beneficiaryNameMap.set(item.cb_idx, item.cb_name);
      }
    });

    const finalMatches: Record<number, { cb_idx: number; cb_name: string; matchType: string }> = {};
    const itemsNeedingAutoMatch: Array<{ idx: number; memo: string; price: number }> = [];

    targetItems.forEach((item) => {
      if (item.matchedBeneficiaryIdx) {
        finalMatches[item.idx] = {
          cb_idx: item.matchedBeneficiaryIdx,
          cb_name: beneficiaryNameMap.get(item.matchedBeneficiaryIdx) || '',
          matchType: 'manual',
        };
      } else if (item.memo) {
        itemsNeedingAutoMatch.push({ idx: item.idx, memo: item.memo, price: item.price });
      }
    });

    if (itemsNeedingAutoMatch.length > 0) {
      const res = await beneficiaryApi.matchFromMemo(Number(memberCode.value), itemsNeedingAutoMatch);
      const memoMatches = res.data?.success ? (res.data.data || {}) : {};
      Object.entries(memoMatches).forEach(([cbIdxStr, match]: [string, any]) => {
        const cashbookIdx = parseInt(cbIdxStr, 10);
        if (!finalMatches[cashbookIdx]) {
          finalMatches[cashbookIdx] = match;
        }
      });
    }

    beneficiaryMatches.value = finalMatches;
  } catch (error) {
    console.error('수급자 매칭 오류:', error);
    beneficiaryMatches.value = {};
  }
};

const loadCashbookData = async () => {
  if (!memberCode.value) return;
  try {
    const res = await bankbookApi.getListWithSplits(memberCode.value, {
      bizGubun: selectedBizGubun.value || undefined,
      year: currentYear.value,
      month: currentMonth.value,
    });
    const data = res.data?.data || [];
    cashbookList.value = data.map((item: CashbookItem) => ({
      ...item,
      cb_price: Number(item.cb_price) || 0,
      split_count: Number(item.split_count) || 0,
    }));
    await loadCarryover();
    await matchBeneficiaries();
  } catch (error) {
    console.error('통장내역 데이터 로드 오류:', error);
    cashbookList.value = [];
    prevMonthBalance.value = 0;
    beneficiaryMatches.value = {};
  }
};

const loadInitialData = async () => {
  if (initialDataLoaded.value) return;
  initialDataLoaded.value = true;
  isLoading.value = true;
  try {
    await loadBankList();
    await loadAccountCodes();
    await loadCashbookData();
  } finally {
    isLoading.value = false;
  }
};

watch(selectedBizGubun, async (newBizGubun, oldBizGubun) => {
  if (newBizGubun && !initialDataLoaded.value) {
    await loadInitialData();
    return;
  }
  if (newBizGubun !== oldBizGubun && initialDataLoaded.value) {
    await loadBankList();
    await loadCashbookData();
  }
}, { immediate: true });

watch([currentYear, currentMonth], async () => {
  if (!initialDataLoaded.value) return;
  if (selectedBankCode.value) {
    const selectedBank = bankList.value.find((b) => b.mb_code === selectedBankCode.value);
    if (selectedBank && !isBankActiveForCurrentMonth(selectedBank)) {
      selectedBankCode.value = null;
    }
  }
  await loadAccountCodes();
  await loadCashbookData();
});

watch(selectedBankCode, async () => {
  if (!initialDataLoaded.value) return;
  await loadCarryover();
});

onMounted(async () => {
  if (selectedBizGubun.value && !initialDataLoaded.value) {
    await loadInitialData();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, [
          _createVNode(_component_v_icon, {
            size: "18",
            color: "white"
          }, {
            default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
              _createTextVNode("mdi-file-chart-outline", -1)
            ]))]),
            _: 1
          })
        ]),
        _cache[2] || (_cache[2] = _createElementVNode("div", { class: "title-text" }, [
          _createElementVNode("h2", null, "회계보고서류"),
          _createElementVNode("p", null, "장부 · 회계분석 · 품의·결의서")
        ], -1))
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createElementVNode("button", {
          type: "button",
          class: _normalizeClass(["bank-chip", { active: selectedBankCode.value === null }]),
          onClick: _cache[0] || (_cache[0] = ($event: any) => (selectBank(null)))
        }, " 전체 ", 2),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(activeBankList.value, (bank) => {
          return (_openBlock(), _createElementBlock("button", {
            key: bank.mb_code,
            type: "button",
            class: _normalizeClass(["bank-chip", { active: selectedBankCode.value === bank.mb_code }]),
            onClick: ($event: any) => (selectBank(bank.mb_code))
          }, _toDisplayString(bank.mb_alias || bank.mb_bank_name), 11, _hoisted_6))
        }), 128)),
        (bankList.value.length === 0 && !isLoading.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_7, "등록된 통장이 없습니다"))
          : _createCommentVNode("", true)
      ])
    ]),
    (isLoading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
          _createVNode(_component_v_progress_circular, {
            indeterminate: "",
            color: "primary",
            size: "40"
          }),
          _cache[3] || (_cache[3] = _createElementVNode("p", null, "보고 자료를 불러오는 중...", -1))
        ]))
      : (_openBlock(), _createElementBlock("div", _hoisted_9, [
          _createVNode(BankbookReportsModal, {
            page: "",
            "member-code": String(memberCode.value || ''),
            "current-year": currentYear.value,
            "current-month": currentMonth.value,
            "biz-gubun": selectedBizGubun.value,
            "bank-code": selectedBankCode.value,
            "bank-label": selectedBankAlias.value,
            "cashbook-list": reportCashbookList.value,
            "prev-month-balance": prevMonthBalance.value,
            "income-code-list": incomeCodeList.value,
            "expense-code-list": expenseCodeList.value,
            "beneficiary-matches": beneficiaryMatches.value,
            onChangeYearMonth: onChangeYearMonth
          }, null, 8, ["member-code", "current-year", "current-month", "biz-gubun", "bank-code", "bank-label", "cashbook-list", "prev-month-balance", "income-code-list", "expense-code-list", "beneficiary-matches"])
        ]))
  ]))
}
}

})